import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Pencil, Calendar, Target, CheckCircle, XCircle } from "lucide-react";
import "./EditGoal.css";
import { toApiDate1 } from "../utils/DateUtils";
import openNotification from "components/utils/CustomNotification";

const API_URL = process.env.REACT_APP_API_URL;

const EditGoal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionUid = localStorage.getItem("session_uid") ?? "";
  const userUid = localStorage.getItem("user_uid") ?? "";

  const parseUTCDate = (datetime: string | null | undefined): Date | null => {
    if (!datetime) return null;
    const date = new Date(datetime);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  };

  // const formatDateToUTC = (date: Date | null): string => {
  //     if (!date) return "";
  //     return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  //         .toISOString()
  //         .replace(".000Z", "Z");
  // };

  const [goal, setGoal] = useState(
    location.state?.goal
      ? {
        ...location.state.goal,
        from_datetime: parseUTCDate(location.state.goal.from_datetime),
        to_datetime: parseUTCDate(location.state.goal.to_datetime),
        goal_metric_value: String(location.state.goal.goal_metric_value ?? ""), // ✅ ensure string
        goal_name: location.state.goal.goal_name ?? "",
        description: location.state.goal.description ?? "",
      }
      : {
        goal_uid: "",
        goal_name: "",
        description: "",
        goal_metric_type: "steps",
        goal_metric_value: "",
        from_datetime: null,
        to_datetime: null,
        goal_category: "fitness",
        goal_type: "daily",
      }
  );


  const handleUpdateGoal = async () => {
    if (!goal.goal_uid) {
      alert("Invalid goal. Please go back and try again.");
      return;
    }

    if (!goal.from_datetime || !goal.to_datetime) {
      alert("Please select valid start and end dates.");
      return;
    }

    if (!goal.goal_metric_value || isNaN(Number(goal.goal_metric_value))) {
      alert("Please enter a valid goal value.");
      return;
    }

    const formattedGoal = {
      ...goal,
      from_datetime: toApiDate1(goal.from_datetime),
      to_datetime: toApiDate1(goal.to_datetime),
    };

    try {
      const response = await fetch(`${API_URL}/api/goals`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        },credentials: "include",
        body: JSON.stringify(formattedGoal),
      });

      const result = await response.json();

      if (response.ok && result.status === "Success") {
        openNotification({
          type: "success",
          message: `Goal Updated Successfully`,
        });
        navigate("/goal-settings");
      } else {
        openNotification({
          type: "error",
          message: `Failed to Update Goal: ${result.data || "Something went wrong."}`,
        });
      }
    } catch (error) {
      console.error("Error updating goal:", error);
      openNotification({
        type: "error",
        message: "Network Error: Something went wrong while updating the goal.",
      });
    }
  };


  return (
    <div className="edit-goal">
      <h2 className="editGoal-h2">
        <Pencil size={24} /> Edit Goal
      </h2>
      {goal.goal_name && (
        <div className="editGoal-subheading">
          <Target size={16} style={{ marginRight: "6px" }} />
          <strong>{goal.goal_name}</strong> • {goal.goal_metric_type}
        </div>
      )}

      <div className="editGoal-form-group">
        <label>Goal Name</label>
        <input
          type="text"
          placeholder="Enter Goal Name"
          value={goal.goal_name}
          onChange={(e) => setGoal({ ...goal, goal_name: e.target.value })}
          maxLength={30}
        />
      </div>

      <div className="editGoal-form-group">
        <label>Description</label>
        <input
          type="text"
          placeholder="Enter Description"
          value={goal.description}
          onChange={(e) => setGoal({ ...goal, description: e.target.value })}
          maxLength={30}
        />
      </div>

      {/* <div className="editGoal-form-group">
                <label>Metric Type</label>
                <select
                    value={goal.goal_metric_type}
                    onChange={(e) => setGoal({ ...goal, goal_metric_type: e.target.value })}
                >
                    <option value="steps">Steps</option>
                    <option value="distance">Distance</option>
                    <option value="calories">Calories</option>
                </select>
            </div> */}

      <div className="editGoal-form-group">
        <label>Goal Value</label>
        <input
          type="number"
          placeholder="Enter Goal Value"
          value={goal.goal_metric_value}
          onChange={(e) => {
            let value = e.target.value;

            // limit to 5 digits
            if (value.length > 5) {
              value = value.slice(0, 6);
            }

            setGoal({ ...goal, goal_metric_value: value });
          }}
        />

      </div>

      {/* <div className="editGoal-form-group">
                <label>
                    <Calendar size={16} /> From DateTime
                </label>
                <DatePicker
                    selected={goal.from_datetime}
                    onChange={(date) => setGoal({ ...goal, from_datetime: date })}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="yyyy-MM-dd HH:mm"
                    className="editGoal-date-picker"
                />
            </div>

            <div className="editGoal-form-group">
                <label>
                    <Calendar size={16} /> To DateTime
                </label>
                <DatePicker
                    selected={goal.to_datetime}
                    onChange={(date) => setGoal({ ...goal, to_datetime: date })}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="yyyy-MM-dd HH:mm"
                    className="editGoal-date-picker"
                />
            </div>

            <div className="editGoal-form-group">
                <label>Goal Period</label>
                <select
                    value={goal.goal_type}
                    onChange={(e) => setGoal({ ...goal, goal_type: e.target.value })}
                >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                </select>
            </div> */}

      <div className="editGoal-button-group">
        <button onClick={handleUpdateGoal} className="editGoal-save-btn">
          <CheckCircle size={18} /> Update
        </button>
        <button onClick={() => navigate("/goal-settings")} className="editGoal-cancel-btn">
          <XCircle size={18} /> Cancel
        </button>
      </div>
    </div>
  );
};

export default EditGoal;
